import { Link } from 'react-router-dom';
import { useRef } from 'react';
import gsap from 'gsap';
import { useGSAP } from '@gsap/react';
import Navbar from './Navbar';
import Footer from './Footer';
import projectsData from '../data/projectsData';

function ProjectRow({ project, index }) {
  return (
    <article style={{
      display: 'grid',
      gridTemplateColumns: '280px 1fr',
      gap: '2.5rem',
      padding: '2.5rem 0',
      borderBottom: '1px solid var(--border)',
      alignItems: 'start',
    }} className="proj-row">
      {/* Thumbnail */}
      <div style={{ overflow: 'hidden', borderRadius: '2px', border: '1px solid var(--border)', aspectRatio: '16/10', flexShrink: 0 }}>
        <img
          src={project.image} alt={project.title}
          style={{
            width: '100%', height: '100%', objectFit: 'cover', display: 'block',
            filter: 'grayscale(30%)',
            transition: 'transform 0.5s ease, filter 0.3s ease',
          }}
          onMouseEnter={e => { e.currentTarget.style.transform = 'scale(1.06)'; e.currentTarget.style.filter = 'grayscale(0%)'; }}
          onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; e.currentTarget.style.filter = 'grayscale(30%)'; }}
        />
      </div>

      {/* Info */}
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.5rem' }}>
          <span className="label-gold">Project {String(index + 1).padStart(2, '0')}</span>
        </div>
        <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '1.75rem', marginBottom: '0.75rem', color: 'var(--white)', lineHeight: 1.15 }}>
          <Link to={project.details} style={{ color: 'inherit', textDecoration: 'none' }}>
            {project.title}
          </Link>
        </h3>
        <p style={{ color: 'var(--muted)', fontSize: '0.95rem', lineHeight: 1.8, marginBottom: '1.25rem', maxWidth: '60ch' }}>
          {project.summary}
        </p>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem', marginBottom: '1.5rem' }}>
          {project.tech.map(t => <span key={t} className="tag">{t}</span>)}
        </div>
        <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap' }}>
          <Link to={project.details} className="inline-link" style={{ fontSize: '0.85rem' }}>Case Study →</Link>
          <a href={project.github} target="_blank" rel="noopener noreferrer" className="inline-link" style={{ fontSize: '0.85rem' }}>GitHub →</a>
          {project.demo !== '#' && (
            <a href={project.demo} target="_blank" rel="noopener noreferrer" className="inline-link" style={{ fontSize: '0.85rem' }}>Live Demo →</a>
          )}
        </div>
      </div>

      <style>{`
        @media (max-width: 768px) {
          .proj-row { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </article>
  );
}

export default function ViewAllProjects() {
  const container = useRef();

  useGSAP(() => {
    gsap.from('.proj-row', {
      y: 40, opacity: 0, duration: 0.8,
      stagger: 0.12, ease: 'power3.out',
    });
  }, { scope: container });

  return (
    <>
      <Navbar />
      <main style={{ paddingTop: '6rem', minHeight: '100vh' }}>
        <div className="page-wrap" style={{ paddingTop: '5rem', paddingBottom: '8rem' }}>
          {/* Header */}
          <div style={{ marginBottom: '4rem' }}>
            <Link to="/" className="inline-link" style={{ fontSize: '0.85rem', display: 'inline-flex', alignItems: 'center', gap: '0.4rem', marginBottom: '2rem', display: 'block' }}>
              ← Back Home
            </Link>
            <span className="sec-num" style={{ fontSize: 'clamp(4rem, 8vw, 7rem)' }}>All</span>
            <h1 className="display-lg" style={{ marginTop: '-1rem' }}>Projects</h1>
            <p style={{ color: 'var(--muted)', fontSize: '1.05rem', maxWidth: '50ch', marginTop: '1rem', lineHeight: 1.8 }}>
              A complete collection of projects — spanning AI automation, web development, portfolio websites, and fintech dashboards.
            </p>
          </div>

          <hr className="hr" />

          {/* Project list */}
          <div ref={container}>
            {projectsData.map((project, index) => (
              <ProjectRow key={project.id} project={project} index={index} />
            ))}
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}